// OrderManagement.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { orderService } from '../../services/orderService';
import { Order } from '../../types';

const OrderManagement: React.FC = () => {
    const { user } = useAuth();
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);

    useEffect(() => {
        loadOrders();
    }, [statusFilter]);

    const loadOrders = async () => {
        setLoading(true);
        try {
            const response = await orderService.getAllOrders();
            let filteredOrders = response.orders;
            if (statusFilter) {
                filteredOrders = filteredOrders.filter((order: Order) => order.status === statusFilter);
            }
            setOrders(filteredOrders);
        } catch (error) {
            console.error('Erreur lors du chargement des commandes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (orderId: number, newStatus: string) => {
        try {
            await orderService.updateOrderStatus(orderId, newStatus);
            loadOrders();
        } catch (error) {
            alert('Erreur lors de la mise à jour du statut');
        }
    };

    if (user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-eldai-gray-light flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h1>
                    <p className="text-eldai-gray">Vous n'avez pas les permissions pour accéder à cette page.</p>
                    <Link to="/" className="btn btn-primary mt-4 inline-block">
                        Retour à l'accueil
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-eldai-gray-light">
            <div className="container mx-auto px-4 py-8">
                <div className="mb-8">
                    <div className="flex items-center mb-4">
                        <Link to="/admin" className="text-eldai-blue hover:text-eldai-light-blue mr-4 flex items-center">
                            <span className="text-2xl mr-2">←</span> Retour au tableau de bord
                        </Link>
                    </div>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-eldai-blue to-eldai-yellow bg-opacity-10 rounded-lg p-6 shadow">
                        <div>
                            <h1 className="text-4xl font-extrabold text-eldai-blue mb-2 flex items-center">
                                <span className="mr-3 text-eldai-yellow">📦</span> Gestion des commandes
                            </h1>
                            <p className="text-eldai-gray text-lg">
                                Suivez et gérez toutes les commandes clients.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="mb-4 flex space-x-2">
                    <button
                        className={`px-4 py-2 rounded ${!statusFilter ? 'bg-eldai-blue text-white' : 'bg-white text-eldai-blue border border-eldai-blue'}`}
                        onClick={() => setStatusFilter(null)}
                    >
                        Toutes
                    </button>
                    <button
                        className={`px-4 py-2 rounded ${statusFilter === 'pending' ? 'bg-eldai-blue text-white' : 'bg-white text-eldai-blue border border-eldai-blue'}`}
                        onClick={() => setStatusFilter('pending')}
                    >
                        En attente
                    </button>
                    <button
                        className={`px-4 py-2 rounded ${statusFilter === 'confirmed' ? 'bg-eldai-blue text-white' : 'bg-white text-eldai-blue border border-eldai-blue'}`}
                        onClick={() => setStatusFilter('confirmed')}
                    >
                        Confirmées
                    </button>
                    <button
                        className={`px-4 py-2 rounded ${statusFilter === 'preparing' ? 'bg-eldai-blue text-white' : 'bg-white text-eldai-blue border border-eldai-blue'}`}
                        onClick={() => setStatusFilter('preparing')}
                    >
                        Préparation
                    </button>
                    <button
                        className={`px-4 py-2 rounded ${statusFilter === 'shipped' ? 'bg-eldai-blue text-white' : 'bg-white text-eldai-blue border border-eldai-blue'}`}
                        onClick={() => setStatusFilter('shipped')}
                    >
                        Expédiées
                    </button>
                    <button
                        className={`px-4 py-2 rounded ${statusFilter === 'delivered' ? 'bg-eldai-blue text-white' : 'bg-white text-eldai-blue border border-eldai-blue'}`}
                        onClick={() => setStatusFilter('delivered')}
                    >
                        Livrées
                    </button>
                    <button
                        className={`px-4 py-2 rounded ${statusFilter === 'cancelled' ? 'bg-eldai-blue text-white' : 'bg-white text-eldai-blue border border-eldai-blue'}`}
                        onClick={() => setStatusFilter('cancelled')}
                    >
                        Annulées
                    </button>
                </div>
                <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-eldai-gray-light">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gradient-to-r from-eldai-blue to-eldai-yellow bg-opacity-10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">N° Commande</th>
                                    <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Client</th>
                                    <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Date</th>
                                    <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Total</th>
                                    <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Statut</th>
                                    <th className="px-6 py-4 text-left text-base font-bold text-eldai-blue uppercase tracking-wide">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    [...Array(10)].map((_, i) => (
                                        <tr key={i} className="bg-eldai-gray-light">
                                            {[...Array(6)].map((_, j) => (
                                                <td key={j} className="px-6 py-4">
                                                    <div className="h-4 bg-eldai-gray rounded animate-pulse"></div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : orders.length > 0 ? (
                                    orders.map((order, idx) => (
                                        <tr key={order.id} className={idx % 2 === 0 ? "bg-white hover:bg-eldai-gray-light" : "bg-eldai-gray-light hover:bg-eldai-yellow/10"}>
                                            <td className="px-6 py-4 text-sm font-mono text-eldai-blue">{order.order_number}</td>
                                            <td className="px-6 py-4">{order.user_id}</td>
                                            <td className="px-6 py-4">{new Date(order.created_at).toLocaleDateString('fr-FR')}</td>
                                            <td className="px-6 py-4 text-base font-bold text-eldai-blue">{order.total_amount} €</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-sm font-bold shadow ${order.status === 'pending'
                                                        ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                                        : order.status === 'confirmed'
                                                            ? 'bg-blue-100 text-blue-800 border border-blue-200'
                                                            : order.status === 'preparing'
                                                                ? 'bg-purple-100 text-purple-800 border border-purple-200'
                                                                : order.status === 'shipped'
                                                                    ? 'bg-orange-100 text-orange-800 border border-orange-200'
                                                                    : order.status === 'delivered'
                                                                        ? 'bg-green-100 text-green-800 border border-green-200'
                                                                        : order.status === 'cancelled'
                                                                            ? 'bg-red-100 text-red-800 border border-red-200'
                                                                            : 'bg-eldai-gray text-eldai-blue'
                                                    }`}>
                                                    {order.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                {order.status === 'pending' && (
                                                    <button
                                                        className="bg-eldai-blue text-white px-4 py-2 rounded-lg font-bold shadow hover:bg-eldai-light-blue transition-colors"
                                                        onClick={() => handleStatusChange(order.id, 'confirmed')}
                                                    >
                                                        Confirmer
                                                    </button>
                                                )}
                                                {order.status === 'confirmed' && (
                                                    <button
                                                        className="bg-yellow-600 text-white px-4 py-2 rounded-lg font-bold shadow hover:bg-yellow-700 transition-colors"
                                                        onClick={() => handleStatusChange(order.id, 'preparing')}
                                                    >
                                                        Passer en préparation
                                                    </button>
                                                )}
                                                {order.status === 'preparing' && (
                                                    <button
                                                        className="bg-orange-600 text-white px-4 py-2 rounded-lg font-bold shadow hover:bg-orange-700 transition-colors"
                                                        onClick={() => handleStatusChange(order.id, 'shipped')}
                                                    >
                                                        Marquer comme expédiée
                                                    </button>
                                                )}
                                                {order.status === 'shipped' && (
                                                    <button
                                                        className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold shadow hover:bg-green-700 transition-colors"
                                                        onClick={() => handleStatusChange(order.id, 'delivered')}
                                                    >
                                                        Marquer comme livrée
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-16 text-center bg-eldai-gray-light rounded-b-xl">
                                            <div className="text-7xl mb-4">📦</div>
                                            <h3 className="text-2xl font-bold text-eldai-blue mb-2">
                                                Aucune commande trouvée
                                            </h3>
                                            <p className="text-eldai-gray mb-4 text-lg">
                                                Il n'y a pas encore de commandes à afficher.
                                            </p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderManagement;
